import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import ScrollNavigation from '@/components/ScrollNavigation'
import { CompatibilityModuleProps } from '../types/compatibility.types'
import { compatibilityItems } from '../data/compatibilityData'
import './CompatibilityModule.css'

const CompatibilityModule: React.FC<CompatibilityModuleProps> = ({
    title = 'Compatible with your favourites',
    subtitle = 'Kibu headphones use a standard 3.5mm audio jack (aux), so they work with almost everything kids love.',
    items = compatibilityItems,
    className = '',
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [canScrollPrev, setCanScrollPrev] = useState(false)
    const [canScrollNext, setCanScrollNext] = useState(false)
    const [scrollProgress, setScrollProgress] = useState(5)
    const [needsScroll, setNeedsScroll] = useState(false)
    const [selectedItem, setSelectedItem] = useState<(typeof items)[number] | null>(null)

    const boldPhrase = '3.5mm audio jack (aux)'
    const renderedSubtitle = subtitle && subtitle.includes(boldPhrase)
        ? (() => {
            const [before, after] = subtitle.split(boldPhrase)
            return (
                <>
                    {before}
                    <strong>{boldPhrase}</strong>
                    {after}
                </>
            )
        })()
        : subtitle

    const getCardScrollStep = (scrollContainer: HTMLDivElement) => {
        const firstCard = scrollContainer.querySelector<HTMLElement>('.compat-card')
        if (!firstCard) return 0
        const computedStyles = window.getComputedStyle(scrollContainer)
        const gap = parseFloat(computedStyles.columnGap || computedStyles.gap || '0')
        return firstCard.offsetWidth + gap
    }

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current
        if (!scrollContainer) return

        const updateScrollState = () => {
            const currentScroll = scrollContainer.scrollLeft
            const maxScrollLeft = Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0)

            setNeedsScroll(maxScrollLeft > 1)
            setCanScrollPrev(currentScroll > 1)
            setCanScrollNext(currentScroll < maxScrollLeft - 1)

            if (maxScrollLeft <= 1) {
                setScrollProgress(100)
                return
            }
            setScrollProgress(currentScroll <= 1 ? 5 : (currentScroll / maxScrollLeft) * 100)
        }

        scrollContainer.addEventListener('scroll', updateScrollState, { passive: true })
        window.addEventListener('resize', updateScrollState)

        let resizeObserver: ResizeObserver | null = null
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateScrollState)
            resizeObserver.observe(scrollContainer)
        }

        updateScrollState()

        return () => {
            scrollContainer.removeEventListener('scroll', updateScrollState)
            window.removeEventListener('resize', updateScrollState)
            resizeObserver?.disconnect()
        }
    }, [])

    const handleNavigation = (direction: 'prev' | 'next') => {
        const scrollContainer = scrollContainerRef.current
        if (!scrollContainer) return
        const scrollStep = getCardScrollStep(scrollContainer)
        if (!scrollStep) return
        const maxScrollLeft = Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0)
        const nextPosition = direction === 'next'
            ? Math.min(scrollContainer.scrollLeft + scrollStep, maxScrollLeft)
            : Math.max(scrollContainer.scrollLeft - scrollStep, 0)
        scrollContainer.scrollTo({ left: nextPosition, behavior: 'smooth' })
    }

    useEffect(() => {
        if (!selectedItem) return
        const originalOverflow = window.getComputedStyle(document.body).overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = originalOverflow
        }
    }, [selectedItem])

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            setSelectedItem(null)
        }
    }

    const modalContent = selectedItem ? (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                onClick={handleBackdropClick}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ type: 'spring', damping: 24, stiffness: 280 }}
                    className="relative w-full max-w-lg max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/90 text-gray-500 hover:text-gray-700"
                        aria-label="Close popup"
                        onClick={() => setSelectedItem(null)}
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-gray-50 flex items-center justify-center p-6">
                        <img
                            src={selectedItem.imageUrl}
                            alt={selectedItem.name}
                            className="max-h-56 w-auto object-contain"
                        />
                    </div>

                    <div className="p-6 overflow-y-auto">
                        <h3 className="text-2xl font-headline text-navy-900">{selectedItem.name}</h3>
                        {selectedItem.category && (
                            <p className="text-sm text-gray-400 mt-1">{selectedItem.category}</p>
                        )}
                        <p className="text-gray-600 mt-4 leading-relaxed">
                            {selectedItem.compatibilityNote || 'Works with Kibu headphones.'}
                        </p>
                        {selectedItem.adapterNote && (
                            <p className="text-sm text-amber-700 mt-3">{selectedItem.adapterNote}</p>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    ) : null

    return (
        <section className={`w-full ${className}`}>
            <div className="module_container">
                {/* Header */}
                <div className="flex justify-between items-start mb-8 pl-2">
                    <div className="flex flex-col gap-4">
                        <h2 className="text-4xl md:text-5xl font-headline text-navy-900">{title}</h2>
                        {subtitle && (
                            <p className="text-gray-500 font-body text-base max-w-xl">{renderedSubtitle}</p>
                        )}
                    </div>
                    {needsScroll && (
                        <ScrollNavigation
                            onPrevious={() => handleNavigation('prev')}
                            onNext={() => handleNavigation('next')}
                            canScrollPrevious={canScrollPrev}
                            canScrollNext={canScrollNext}
                            className="ml-4 mt-1"
                        />
                    )}
                </div>

                <div className="relative w-full overflow-visible">
                    <div
                        ref={scrollContainerRef}
                        className="flex gap-5 overflow-x-auto snap-x snap-mandatory hide-scrollbar smooth-scroll pr-6 md:pr-10 py-2"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {items.map((item) => (
                            <div
                                key={item.id}
                                className="compat-card group flex-none w-36 sm:w-52 md:w-44 bg-white rounded-2xl border border-gray-100 shadow-sm snap-start flex flex-col overflow-hidden cursor-pointer transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-900/25"
                                role="button"
                                tabIndex={0}
                                aria-label={`Open compatibility details for ${item.name}`}
                                onClick={() => setSelectedItem(item)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        setSelectedItem(item)
                                    }
                                }}
                            >
                                {/* Image area */}
                                <div className="size-42 bg-gray-50 flex items-center justify-center p-3">
                                    <img
                                        src={item.imageUrl}
                                        alt={item.name}
                                        className="max-h-full max-w-full object-contain transition-transform duration-300 ease-out group-hover:scale-105"
                                        loading="lazy"
                                        onError={(e) => {
                                            const target = e.currentTarget
                                            target.style.display = 'none'
                                            const parent = target.parentElement
                                            if (parent && !parent.querySelector('.compat-fallback')) {
                                                const fallback = document.createElement('div')
                                                fallback.className = 'compat-fallback w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl'
                                                fallback.textContent = item.name.charAt(0)
                                                parent.appendChild(fallback)
                                            }
                                        }}
                                    />
                                </div>

                                {/* Info area */}
                                <div className="p-4 flex flex-col gap-2 flex-1">
                                    <div>
                                        <p className="font-headline text-sm text-gray-900 leading-tight">{item.name}</p>
                                        {item.category && (
                                            <p className="text-xs text-gray-400 mt-0.5">{item.category}</p>
                                        )}
                                    </div>

                                    <div className="mt-auto flex flex-col gap-1">
                                        {item.adapterRequired ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-[9px] md:text-xs font-medium w-fit">
                                                <span>⚡</span> Adapter needed
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 pl-3 pr-4 py-1 rounded-full bg-green text-white text-xs font-medium w-fit">
                                                <span>✓</span> Compatible
                                            </span>
                                        )}
                                        {/* {item.adapterNote && (
                                            <p className="text-xs text-gray-400 leading-tight">{item.adapterNote}</p>
                                        )} */}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Progress bar */}
                    {needsScroll && (
                        <div className="w-full h-1 bg-white mt-6 rounded-full">
                            <div
                                className="h-full bg-kibuBlue transition-all duration-300 ease-out rounded-full"
                                style={{ width: `${Math.min(scrollProgress, 100)}%` }}
                            />
                        </div>
                    )}
                </div>
            </div>
            {selectedItem && typeof document !== 'undefined' && createPortal(modalContent, document.body)}
        </section>
    )
}

export default CompatibilityModule
